/** Pure sample-buffer helpers for the synthesized ambient beds (no Web Audio — unit-tested). */

/** One-pole high-pass: strips DC and sub-bass rumble that would thump speakers. */
export function highpass(d: Float32Array, sampleRate: number, hz: number) {
  const rc = 1 / (2 * Math.PI * hz);
  const a = rc / (rc + 1 / sampleRate);
  let prevIn = d[0] ?? 0;
  let prevOut = 0;
  for (let i = 0; i < d.length; i++) {
    const x = d[i]!;
    prevOut = a * (prevOut + x - prevIn);
    prevIn = x;
    d[i] = prevOut;
  }
}

/**
 * Scales all channels together to `targetRms`, but never past `maxPeak`, so every bed plays at
 * the same loudness and switching between them never jumps in level.
 */
export function normalize(channels: Float32Array[], targetRms: number, maxPeak: number) {
  let sum = 0;
  let n = 0;
  let peak = 0;
  for (const d of channels) {
    let mean = 0;
    for (const v of d) mean += v;
    mean /= d.length || 1;
    for (let i = 0; i < d.length; i++) {
      const v = (d[i]! -= mean);
      sum += v * v;
      peak = Math.max(peak, Math.abs(v));
    }
    n += d.length;
  }
  const rms = Math.sqrt(sum / (n || 1));
  if (!rms || !peak) return;
  const k = Math.min(targetRms / rms, maxPeak / peak);
  for (const d of channels) for (let i = 0; i < d.length; i++) d[i]! *= k;
}

/** Level (0–1) of a linear fade-in that started at `start` and lasts `dur`, at time `now`. */
export const fadeInLevel = (start: number, now: number, dur: number): number =>
  dur > 0 ? Math.min(1, Math.max(0, (now - start) / dur)) : 1;
